import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { jobStatusRules } from "components/History";
import ModalBox from "components/Modal";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import { tagsColors } from "constants/colors";
import { getInitials } from "helpers/utils";
import { useFocusAwarePolling } from "hooks/useFocusAwarePolling";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUserListByCompany } from "store/adminPanelSlice";
import type { Job } from "store/jobsStore";
import { fetchJobsPaginated, forceUpdateJobStatus, ProcessingJobStatus } from "store/jobsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import ChangeProcessingJobStatus from "./ChangeProcessingJobStatusModal";

export const status = ["En attente", "En cours", "Terminé", "En échec", "Annulé"];

const PAGE_LIMIT = 15;

const ImportTracking = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { allJobs, allJobsPagination, allJobsStatus } = useAppSelector((state) => state.jobs);
    const { data: users } = useAppSelector((state) => state.adminPanel.users.company);
    const [showEditStatus, setShowEditStatus] = useState<Job | false>(false);
    const [page, setPage] = useState(0); // 0-indexed for MUI TablePagination

    useEffect(() => {
        dispatch(fetchJobsPaginated({ page: page + 1, limit: PAGE_LIMIT }));
    }, [dispatch, page]);

    const hasRunningJob = allJobs.some(
        (job) => job.status === ProcessingJobStatus.PROCESSING || job.status === ProcessingJobStatus.QUEUED,
    );

    useFocusAwarePolling({
        enabled: hasRunningJob,
        intervalMs: 6000,
        pollImmediately: false,
        onPoll: () => {
            dispatch(fetchJobsPaginated({ page: page + 1, limit: PAGE_LIMIT, silent: true }));
        },
    });

    useEffect(() => {
        if (!users.length) dispatch(getUserListByCompany());
    }, []);

    const normalizedData = allJobs.map((job) => ({ ...job, action: "Modifier le statut" }));

    /**
     * Internal page,
     * it doesn't matter if there's no i18n
     */
    const columns: LastElementTableColumns[] = [
        { id: "id", label: "ID" },
        { id: "importType", label: "Nature de la tâche", format: (value: string) => t(`History.Type.${value}`) },
        { id: "status", label: "Statut", format: (value: number, id?: number) => renderStatus(value, id) },
        {
            id: "progress",
            label: "Avancement",
            format: (value: string | number) => {
                const numeric = String(value ?? "")
                    .replace(/%/g, "")
                    .trim();
                return numeric === "" ? "0 %" : `${numeric} %`;
            },
        },
        { id: "createdBy", label: "Initié par", format: (value: number, id?: number) => renderUser(value, id) },
        { id: "createdAt", label: "Début de la tâche", format: (value: string) => renderDate(value) },
        { id: "modifiedAt", label: "Dernière mise à jour", format: (value: string) => renderDate(value) },
        {
            id: "action",
            label: "",
            align: "center",
            format: (value, id, row) => (
                <div
                    style={{ color: "#9B5067", fontWeight: "600", cursor: "pointer" }}
                    onClick={() => setShowEditStatus(row)}
                >
                    {value}
                </div>
            ),
        },
    ];

    const renderStatus = (value: ProcessingJobStatus, _id?: number) => {
        const color = tagsColors[value];
        return (
            <Chip
                size="small"
                label={jobStatusRules(t)[value].text}
                sx={{ backgroundColor: color.backgroundColor, color: color.textColor }}
            />
        );
    };

    const renderUser = (value: number, _id?: number) => {
        const user = users.find((u) => +u.id === +value);
        if (user) {
            return (
                <div style={{ display: "flex", justifyContent: "space-evenly", alignItems: "center" }}>
                    <Avatar
                        sx={{
                            backgroundColor: "rgba(184, 174, 233, 0.2)",
                            color: variables["foreground"],
                            fontSize: 10,
                            maxWidth: "28px",
                            maxHeight: "28px",
                        }}
                    >
                        {getInitials(`${user.firstname ?? ""} ${user.lastname ?? ""}`)}
                    </Avatar>
                    <div>
                        <div>
                            {user.firstname ?? ""} {user.lastname ?? ""}
                        </div>
                    </div>
                </div>
            );
        }
        return value;
    };

    const renderDate = (value: string) => new Date(value).toLocaleString("fr-FR");

    const handleChangeStatus = async (newStatus: ProcessingJobStatus) =>
        dispatch(forceUpdateJobStatus({ jobId: (showEditStatus as Job).id, status: newStatus }));

    const apiData: LastElementTableAPIData = {
        count: allJobsPagination.total,
        page,
        limitPagination: PAGE_LIMIT,
        handleChangePage: (_: unknown, newPage: number) => setPage(newPage),
        handleChangeLimit: () => {},
        handleSort: () => {},
        requestStatus: allJobsStatus,
    };

    return (
        <div className="log-list-container">
            <ModalBox
                onClose={() => setShowEditStatus(false)}
                mainTitle={"Modifier le statut"}
                open={showEditStatus !== false}
            >
                <ChangeProcessingJobStatus
                    processingJob={showEditStatus as Job}
                    close={() => setShowEditStatus(false)}
                    onChangeStatus={handleChangeStatus}
                />
            </ModalBox>

            <LastElementTable
                columns={columns}
                data={normalizedData}
                onShowAll={() => {}}
                max={-1}
                APIData={apiData}
                dataCount={allJobsPagination.total}
            />
        </div>
    );
};

export default ImportTracking;
